import { LitElement, css, html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { METALLIC_LEVEL, networkFilterOrder, lobNetworkLabel, LOB } from '../../util/plans';
import { consume } from '@lit/context';
import { inputResponseContext } from '../../contexts/inputResponse';
import { BackendFormResponse, Producer } from '../../util/api';
import { Filters } from '../../util/filter-sort';
import { EVENT, makeCustomEvent } from '../../util/events';
import { filtersContext } from '../../contexts/filters';
import { breakpointContext } from '../../contexts/breakpoint';
import { BREAKPOINT } from '../../util/breakpoints';
import '../../components/sort-select';
import { agentContext } from '../../contexts/agent';

@customElement('rq-filter-sort')
export class FilterSortCard extends LitElement {
    static styles = css`
        .filters {
            display: flex;
            flex-direction: column;
            gap: 16px;
            width: 100%;
            position: sticky;
            top: 0;
        }
        .title {
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            align-items: center;
        }
        .filter-label {
            font-size: 24px;
        }
    `;

    @query('#offMarketplace')
    offMarketplaceRadio!: HTMLInputElement;

    @state()
    showMarketplaceModal = false;

    @consume({ context: inputResponseContext, subscribe: true })
    inputResponse?: BackendFormResponse;

    @consume({ context: filtersContext, subscribe: true })
    @state()
    filters!: Filters;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: agentContext, subscribe: true })
    agent?: Producer;

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener('lh-hide', this.hideModal);
        this.addEventListener('lh-change', this.listenerHandler as EventListener);
        this.addEventListener('lh-input', this.listenerHandler as EventListener);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('lh-hide', this.hideModal);
        this.removeEventListener('lh-change', this.listenerHandler as EventListener);
        this.removeEventListener('lh-input', this.listenerHandler as EventListener);
    }

    private listenerHandler({ detail: { name, value } }: CustomEvent<{ name: keyof Filters; value: unknown }>) {
        let filters = { ...this.filters };
        if (name === 'productType') {
            // We need to treat LOB.BHOM and LOB.BHN and LOB.ICON as the same for filtering purposes
            let productType = filters.productType;
            if (productType.includes(value as LOB)) {
                productType = productType.filter((n) => {
                    if (value === LOB.BHOM || value === LOB.BHNV || value === LOB.ICON) {
                        return n !== LOB.BHOM && n !== LOB.BHNV && n !== LOB.ICON;
                    }
                    return n !== value;
                });
            } else {
                productType.push(value as LOB);
                if (value === LOB.BHOM || value === LOB.BHNV || value === LOB.ICON) {
                    [LOB.BHOM, LOB.BHNV, LOB.ICON].forEach((lob) => {
                        if (!productType.includes(lob)) {
                            productType.push(lob);
                        }
                    });
                }
            }

            filters.productType = productType;
        } else if (name === 'metallicLevel') {
            const metallicLevel = filters.metallicLevel.includes(value as METALLIC_LEVEL)
                ? filters.metallicLevel.filter((n) => n !== value)
                : [...filters.metallicLevel, value as METALLIC_LEVEL];
            filters.metallicLevel = metallicLevel;
        } else {
            filters = { ...filters, [name]: value };
        }
        this.dispatchEvent(makeCustomEvent(EVENT.SET_FILTER_SORT, { filters }));
    }

    private hideModal() {
        this.showMarketplaceModal = false;
    }

    private showModal() {
        this.showMarketplaceModal = true;
    }

    get isShopper() {
        return this.inputResponse?.context?.toLowerCase() === 'shopper';
    }

    private offMarketplaceHandler(e: InputEvent) {
        if (!this.isShopper) {
            return;
        }
        if (!this.showMarketplaceModal) {
            e.preventDefault();
            this.showModal();
        } else {
            const input: HTMLInputElement | undefined | null = this.offMarketplaceRadio.shadowRoot?.querySelector(
                'input[name="marketplace"]'
            ) as HTMLInputElement;
            if (input) {
                input.click();
            }
            this.hideModal();
        }
    }

    get marketplaceAccordion() {
        if (!this.inputResponse?.subsidy?.subsidyEligible) return nothing;
        return html` <lh-accordion-panel header="Marketplace" default-open>
            <lh-radio-group name="marketplace" default-value=${this.filters.marketplace}>
                <lh-radio value="onExchange">
                    On Marketplace (${this.plans.filter((p) => p.marketplace === 'onExchange').length})
                </lh-radio>
                <lh-radio
                    value="offExchange"
                    id="offMarketplace"
                    @keydown=${this.offMarketplaceHandler}
                    @click=${this.offMarketplaceHandler}
                >
                    Off Marketplace (${this.plans.filter((p) => p.marketplace === 'offExchange').length})
                </lh-radio>
            </lh-radio-group>
        </lh-accordion-panel>`;
    }

    get offMarketplaceModal() {
        if (!this.showMarketplaceModal) return nothing;
        return html`<lh-modal id="marketplaceModal" accessible-label="Off marketplace plans" open>
            <lh-typography type="heading" selector="h3" size="lg"
                >Are you sure you want to view Off Marketplace plans?</lh-typography
            >
            <lh-typography type="text" selector="p" size="md">
                You are now leaving the shopping experience for qualified health and dental plans offered through the
                Marketplace.
            </lh-typography>
            <lh-typography selector="p">
                You must enroll in a Qualified Health Plan offered through the Marketplace to receive financial
                assistance like premium tax credits or cost sharing reductions that could reduce your costs (if
                eligible). You must complete a Marketplace eligibility application to determine if you are eligible for
                financial assistance or other insurance affordability programs like Medicaid or the Children's Health
                Insurance Program (CHIP).
            </lh-typography>
            <lh-container spacing="none" content-width="fluid" gutters="none" margin-top="sm">
                <lh-button-group orientation="horizontal" size="lg" alignment="auto">
                    <lh-button size="lg" variant="outlined" @click=${this.hideModal}>Stay on Marketplace</lh-button>
                    <lh-button size="lg" variant="filled" @click=${this.offMarketplaceHandler}
                        >View Off Marketplace Plans</lh-button
                    >
                </lh-button-group>
            </lh-container>
        </lh-modal>`;
    }

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    get plans() {
        return this.inputResponse?.plans || [];
    }

    render() {
        return html` <div class="filters">
                <div class="title">
                    <lh-typography type="heading" selector="h5" size="lg">
                        <span class="filter-label">Filter ${this.isMobile ? '& Sort' : 'By'}</span>
                    </lh-typography>
                    ${this.isMobile
                        ? html`<lh-icon-button
                              icon="x"
                              accessible-label="Close filters"
                              @click=${() => this.dispatchEvent(makeCustomEvent(EVENT.CLOSE_FILTERS))}
                          ></lh-icon-button> `
                        : nothing}
                </div>
                <lh-accordion>
                    ${this.marketplaceAccordion}
                    <lh-accordion-panel header="Network">
                        <lh-checkbox-group>
                            ${networkFilterOrder.map(
                                (lob) => html`
                                    <lh-checkbox
                                        name="productType"
                                        .value=${lob}
                                        default-checked=${this.filters.productType.includes(lob) ? true : nothing}
                                    >
                                        ${lobNetworkLabel.get(lob) || lob}
                                        (${this.plans.filter((p) => {
                                            if (lob === LOB.BHOM || lob === LOB.BHNV || lob === LOB.ICON) {
                                                return (
                                                    (p.lob === LOB.BHOM || p.lob === LOB.BHNV || p.lob === LOB.ICON) &&
                                                    p.marketplace === this.filters.marketplace
                                                );
                                            }
                                            return p.lob === lob && p.marketplace === this.filters.marketplace;
                                        }).length})
                                    </lh-checkbox>
                                `
                            )}
                        </lh-checkbox-group>
                    </lh-accordion-panel>
                    <lh-accordion-panel header="Metallic Level">
                        <lh-checkbox-group>
                            ${Object.values(METALLIC_LEVEL).map(
                                (metal) => html`
                                    <lh-checkbox
                                        name="metallicLevel"
                                        .value=${metal}
                                        class="capitalize"
                                        default-checked=${this.filters.metallicLevel.includes(metal) ? true : nothing}
                                    >
                                        ${metal}
                                        (${this.plans.filter(
                                            (p) => p.metal === metal && p.marketplace === this.filters.marketplace
                                        ).length})
                                    </lh-checkbox>
                                `
                            )}
                        </lh-checkbox-group>
                    </lh-accordion-panel>
                    <lh-accordion-panel header="Easy Pricing">
                        <lh-checkbox
                            name="easyPricing"
                            default-checked=${this.filters.easyPricing || nothing}
                            .value=${!this.filters.easyPricing}
                        >
                            Easy Pricing
                            (${this.plans.filter(
                                (p) => p.pricing?.toLowerCase() === 'easy' && p.marketplace === this.filters.marketplace
                            ).length})
                        </lh-checkbox>
                    </lh-accordion-panel>
                </lh-accordion>
                ${this.isMobile ? html`<rq-sort-select></rq-sort-select>` : nothing}
            </div>
            ${this.offMarketplaceModal}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-filter-sort': FilterSortCard;
    }
}
